import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { ApiError, api, apiList } from "../../api/client";
import type {
  ParticipantProfile,
  Photo,
  QuestionnaireOut,
  ResolvedProtocol,
  TrendPoint,
} from "../../api/types";

interface DashboardData {
  profile: ParticipantProfile | null;
  protocol: ResolvedProtocol | null;
  photos: Photo[];
  trends: TrendPoint[];
  questionnaires: QuestionnaireOut[];
  hasOneTime: boolean;
  loading: boolean;
  error: string | null;
  reloadProfile: () => Promise<void>;
  reloadResults: () => Promise<void>;
  reloadQuestionnaires: () => Promise<void>;
}

// The shared prefix of every dashboardKeys entry. Invalidation is COARSE on
// purpose (2026-09-01): server writes have cross-resource side effects the
// client cannot see (e.g. the one-time questionnaire submit mirrors `sex`
// onto the participant row), and a hand-maintained per-key invalidation list
// silently goes stale when one is added. Invalidating the whole prefix costs
// a few extra GETs per mutation and removes that bug class. Targeted
// `setQueryData` write-throughs (DailyFlow's gallery) are unaffected.
export const participantScope = ["participant"] as const;

// react-query cache keys for the participant dashboard. Mutations elsewhere
// (e.g. DailyFlow's optimistic gallery update) address the cache by these.
export const dashboardKeys = {
  profile: ["participant", "profile"] as const,
  protocol: ["participant", "protocol"] as const,
  photos: ["participant", "photos"] as const,
  trends: ["participant", "trends"] as const,
  questionnaires: ["participant", "questionnaires"] as const,
};

// A participant who hasn't enrolled yet has no profile: /participants/me 404s.
// That's the onboarding state, not an error — surface it as `null`.
async function fetchProfile(): Promise<ParticipantProfile | null> {
  try {
    return await api<ParticipantProfile>("GET", "/participants/me");
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

// Loads and exposes the participant's profile + photos + trends, plus whether the
// one-time questionnaire is done (which, with an active enrollment, marks onboarding
// complete). Data lives in the react-query cache (30s staleTime, see main.tsx);
// tabs/flows mutate via the API then call the matching reload*, which invalidates
// and refetches the affected keys.
export function useDashboard(): DashboardData {
  const queryClient = useQueryClient();

  const profileQuery = useQuery({ queryKey: dashboardKeys.profile, queryFn: fetchProfile });
  const enrolled = profileQuery.data != null;

  // The study's portal config (study identity + study day + resolved protocol).
  // Refreshed with the profile — consent changes the study day.
  const protocolQuery = useQuery({
    queryKey: dashboardKeys.protocol,
    queryFn: () => api<ResolvedProtocol>("GET", "/participants/me/protocol"),
    enabled: enrolled,
  });
  const photosQuery = useQuery({
    // Paged: the server caps list responses; the gallery renders everything.
    queryKey: dashboardKeys.photos,
    queryFn: () => apiList<Photo>("/participants/me/photos"),
    enabled: enrolled,
  });
  const trendsQuery = useQuery({
    queryKey: dashboardKeys.trends,
    queryFn: () => api<TrendPoint[]>("GET", "/participants/me/trends"),
    enabled: enrolled,
  });
  const questionnairesQuery = useQuery({
    queryKey: dashboardKeys.questionnaires,
    queryFn: () => api<QuestionnaireOut[]>("GET", "/participants/me/questionnaires"),
    enabled: enrolled,
  });

  // All three reloads invalidate the whole participant scope — see the
  // participantScope comment for why coarse beats per-key lists here. The
  // three names survive so call sites still say what changed; concurrent
  // refetches of the same key are deduped by react-query.
  const reloadScope = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: participantScope });
  }, [queryClient]);
  const reloadProfile = reloadScope;
  const reloadResults = reloadScope;
  const reloadQuestionnaires = reloadScope;

  const dependents = [protocolQuery, photosQuery, trendsQuery, questionnairesQuery];
  const loading = profileQuery.isPending || (enrolled && dependents.some((q) => q.isPending));
  const failed = profileQuery.isError || (enrolled && dependents.some((q) => q.isError));

  const questionnaires = questionnairesQuery.data ?? [];
  return {
    profile: profileQuery.data ?? null,
    protocol: protocolQuery.data ?? null,
    photos: photosQuery.data ?? [],
    trends: trendsQuery.data ?? [],
    questionnaires,
    hasOneTime: questionnaires.some((q) => q.questionnaire_type === "one_time"),
    loading,
    error: failed ? "Could not load your dashboard. Please refresh." : null,
    reloadProfile,
    reloadResults,
    reloadQuestionnaires,
  };
}
